import { classifyEmailTier, normalizeEmail } from '@/lib/contact-vault/normalize';
import type { ContactVaultEmailTier, ContactVaultEntry } from '@/types/contact-vault';
import type { Lead } from '@/types/leads';
import type { VaultEntryMatchScore } from '@/lib/contact-vault/match';

export type ContactReviewBucket = 'ready' | 'review' | 'no_match' | 'already_named';

export type ContactResolvableLead = Pick<
  Lead,
  'email' | 'contact_person' | 'contact_resolved_at'
>;

export function isContactResolved(lead: Pick<Lead, 'contact_resolved_at'>): boolean {
  return Boolean(lead.contact_resolved_at);
}

export function vaultEntryMatchesLeadContact(
  lead: Pick<Lead, 'email'>,
  entry: Pick<ContactVaultEntry, 'email_norm' | 'email'>
): boolean {
  const leadEmail = normalizeEmail(lead.email);
  if (!leadEmail) return false;
  const entryEmail = entry.email_norm ?? normalizeEmail(entry.email);
  return Boolean(entryEmail && entryEmail === leadEmail);
}

export function getLeadContactTier(lead: Pick<Lead, 'email' | 'contact_person'>): ContactVaultEmailTier {
  const email = normalizeEmail(lead.email);
  const person = lead.contact_person?.trim() ?? '';
  const [firstName, ...rest] = person.split(/\s+/);
  const lastName = rest.join(' ') || null;
  return classifyEmailTier(email, firstName ?? null, lastName);
}

export function formatLeadContactSummary(lead: Pick<Lead, 'contact_person' | 'email'>): string {
  const name = lead.contact_person?.trim();
  const email = lead.email?.trim();
  if (name && email) return `${name} · ${email}`;
  if (email) return email;
  if (name) return name;
  return 'No contact on file';
}

export function classifyContactReviewBucket(
  lead: Pick<Lead, 'email' | 'contact_person'>,
  bestMatch: VaultEntryMatchScore | null
): ContactReviewBucket {
  const currentTier = getLeadContactTier(lead);

  if (!bestMatch) return 'no_match';

  if (currentTier === 'named' && bestMatch.entry.email_norm === normalizeEmail(lead.email)) {
    return 'already_named';
  }

  if (currentTier === 'named' && bestMatch.confidence !== 'high') {
    return 'already_named';
  }

  const storeLevelReady =
    bestMatch.confidence === 'high' &&
    bestMatch.match_level === 'store' &&
    bestMatch.entry.email_tier === 'named';

  if (
    storeLevelReady &&
    (currentTier === 'unknown' || currentTier === 'generic' || currentTier === 'role')
  ) {
    return 'ready';
  }

  if (storeLevelReady && currentTier !== 'named') {
    return 'ready';
  }

  return 'review';
}

export function leadNeedsContactReview(lead: Pick<Lead, 'email' | 'contact_person'>): boolean {
  const tier = getLeadContactTier(lead);
  return tier === 'unknown' || tier === 'generic' || tier === 'role';
}

/** Week-queue / YAMM cannot send these — no named person+email on the lead card. */
export function leadLacksNamedContact(
  lead: Pick<Lead, 'email' | 'contact_person'>
): boolean {
  return getLeadContactTier(lead) !== 'named';
}

export const NO_CONTACT_CLEANUP_TAG = '[cleanup:no_contact]';

export function buildNoContactCleanupNote(existing: string | null | undefined): string {
  const note = `${NO_CONTACT_CLEANUP_TAG} No named contact on file; removed from Track A Prospecting.`;
  const current = existing?.trim() ?? '';
  if (!current) return note;
  if (current.includes(NO_CONTACT_CLEANUP_TAG)) return current;
  return `${current}\n${note}`;
}

export type ContactAttention = 'ok' | 'apply_vault' | 'review_match' | 'needs_contact' | 'no_vault' | 'unknown';

export const contactAttentionLabels: Record<ContactAttention, string> = {
  ok: 'Contact OK',
  apply_vault: 'Apply vault',
  review_match: 'Review match',
  needs_contact: 'Needs contact',
  no_vault: 'No vault match',
  unknown: 'No email',
};

export function getContactAttention(
  lead: ContactResolvableLead,
  bucket: ContactReviewBucket | null
): ContactAttention {
  if (isContactResolved(lead)) return 'ok';

  if (!lead.email?.trim()) {
    if (bucket === 'ready') return 'apply_vault';
    if (bucket === 'review') return 'review_match';
    if (bucket === 'no_match') return 'needs_contact';
    return 'unknown';
  }

  if (bucket === 'already_named') return 'ok';
  if (bucket === 'ready') return 'apply_vault';
  if (bucket === 'review') return 'review_match';
  if (bucket === 'no_match') {
    return leadNeedsContactReview(lead) ? 'needs_contact' : 'no_vault';
  }
  return leadNeedsContactReview(lead) ? 'needs_contact' : 'ok';
}

export function canApplyVaultSuggestion(
  lead: Pick<Lead, 'email' | 'contact_resolved_at'>,
  entry: Pick<ContactVaultEntry, 'email_norm' | 'email'>,
  attention: ContactAttention
): boolean {
  if (vaultEntryMatchesLeadContact(lead, entry)) return false;
  if (isContactResolved(lead)) return false;
  return attention === 'apply_vault' || attention === 'review_match';
}
